import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import OrderSavingsComparisonModule from '../OrderSavingsComparisonModule';
import Link from 'next/link';
import { PlatformPageHero, PlatformPageType } from '@/shared/types/platform-pages';
import { creatorHubLink } from '@/lib/service';
import { useCreateAccountProvider } from '@/lib/context';
import { useRouter } from 'next/router';
import './index.less';
import { scrollTo } from '@/shared/utils/utils';
import FulfillmentBanner from '@/components/Common/FulfillBanner';

interface IProps {
  title: string;
  content: PlatformPageHero;
  type: PlatformPageType;
  redirectUrl?: string;
  utmSource: string;
}

export default function PlatformHero({ title, content, type, redirectUrl, utmSource }: IProps) {
  const { showSignupDialogFromSourcing } = useCreateAccountProvider();

  const nodeRef = useRef<HTMLDivElement>(null);
  const heroTitleRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    type === PlatformPageType.DESIGN_STUDIO && (videoRef.current.playbackRate = 0.6);
  }, [type]);

  return (
    <div
      ref={nodeRef}
      className={classNames('platform-page-hero', content.class)}
      style={
        content?.colors?.background
          ? {
              background: content.colors.background,
            }
          : null
      }
    >
      <div className="mesh-bg" />
      <div className="noise" />
      <div className="disappearing-overlay" />
      {type === PlatformPageType.DESIGN_STUDIO && (
        <>
          <div className="bg-mask" />
          <video ref={videoRef} className="hero-bg-video" autoPlay muted playsInline loop width="100%">
            <source src="/videos/aura.mp4" type="video/mp4" />
            <source src="/videos/aura.webm" type="video/webm" />
          </video>
        </>
      )}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      {content.platformIcon && <img className="platform-icon" src={content.platformIcon} alt={title} />}

      <div ref={heroTitleRef} className="hero-body">
        {type === PlatformPageType.LOGISTICS_AND_FULFILLMENT && <FulfillmentBanner onFulfillment />}
        <h1 className={classNames('hero-title', content.class)} style={{ color: content.colors.text }}>
          {title}
        </h1>
        <p className={classNames('hero-description', content.class)} style={{ color: content.colors.text }}>
          {content.description}
        </p>

        <div className="hero-btn-container">
          <div
            className={classNames('hero-btn', 'secondary')}
            onClick={() => {
              if (content?.btnScrollTo) {
                scrollTo(content?.btnScrollTo);
              } else if (content?.btnHubUrl) {
                router.push(content.btnHubUrl);
              } else {
                showSignupDialogFromSourcing('', redirectUrl, 'home', utmSource);
              }
            }}
          >
            <span>{content.btnText}</span>
          </div>
          {content.secondaryBtn && (
            <Link
              className="hero-btn"
              href={
                content.secondaryBtn.creatorHubUrl
                  ? `${creatorHubLink()}${content.secondaryBtn.creatorHubUrl}`
                  : `${content.secondaryBtn.externalUrl}`
              }
              target="_blank"
              rel="noreferrer"
            >
              <span>{content.secondaryBtn.text}</span>
            </Link>
          )}
        </div>
        {type === PlatformPageType.DESIGN_STUDIO && (
          <>
            <div className="ai-for-that">
              <a
                href="https://theresanaiforthat.com/ai/pietra-product-design-studio/?ref=featured&v=1064917"
                target="_blank"
                rel="nofollow"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  width="200"
                  style={{ width: '200px', filter: 'brightness(0) invert(1)' }}
                  src="https://media.theresanaiforthat.com/featured3.png"
                  alt="https://media.theresanaiforthat.com/featured3.png"
                />
              </a>

              <a
                href="https://www.producthunt.com/posts/product-design-studio?utm_source=badge-featured&utm_medium=badge&utm_souce=badge-product&#0045;design&#0045;studio"
                target="_blank"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=451607&theme=light"
                  alt="Product&#0032;Design&#0032;Studio - Use&#0032;AI&#0032;to&#0032;design&#0032;products&#0032;&#0038;&#0032;packaging&#0032;and&#0032;get&#0032;it&#0032;made | Product Hunt"
                  style={{ width: '200px', height: '54px' }}
                  width="200"
                  height="54"
                />
              </a>
            </div>
          </>
        )}
        {type === PlatformPageType.LOGISTICS_AND_FULFILLMENT && <OrderSavingsComparisonModule />}
        <div className="hero-images-container">
          {content.bgImage && (
            <div className="hero-bg-img-container">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img className="hero-bg-img" src={content.bgImage} alt={content.bgImageAltText || title} />
            </div>
          )}
          {content.secondaryImage && (
            // eslint-disable-next-line @next/next/no-img-element
            <img className="hero-secondary-img" src={content.secondaryImage} alt={content.bgImageAltText} />
          )}

          {content.tertiaryImage && (
            // eslint-disable-next-line @next/next/no-img-element
            <img className="hero-tertiary-img" src={content.tertiaryImage} alt={content.bgImageAltText} />
          )}
        </div>
      </div>
      {content.showVideo && (
        <div className="hero-video-container">
          <div className="hero-video">
            <iframe
              src={`https://player.vimeo.com/video/${content.heroVideo}?h=c0b11a40fc&amp;badge=0&amp;autoplay=1&amp;muted=1&amp;autopause=0&amp;player_id=0&amp;app_id=58479`}
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
              title={title}
            ></iframe>
          </div>
          <script async src="https://player.vimeo.com/api/player.js" />
        </div>
      )}
    </div>
  );
}
